import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Download, RefreshCw, LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "mps_admin_pwd";

export default function Admin() {
  const [pwd, setPwd] = useState("");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authPwd, setAuthPwd] = useState("");

  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      setAuthPwd(saved);
      setAuthed(true);
    }
  }, []);

  useEffect(() => {
    if (authed) loadLeads();
    // eslint-disable-next-line
  }, [authed]);

  const login = async (e) => {
    e.preventDefault();
    if (!pwd) return;
    try {
      await axios.post(`${API}/admin/login`, { password: pwd });
      sessionStorage.setItem(STORAGE_KEY, pwd);
      setAuthPwd(pwd);
      setAuthed(true);
      setPwd("");
      toast.success("Welcome");
    } catch {
      toast.error("Invalid password");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setAuthPwd("");
    setLeads([]);
  };

  const loadLeads = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/leads`, {
        headers: { "X-Admin-Password": authPwd },
      });
      setLeads(res.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        toast.error("Session expired");
      } else {
        toast.error("Failed to load leads");
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/leads/export`, {
        headers: { "X-Admin-Password": authPwd },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "leads.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <Header />
        <div className="mx-auto flex max-w-md flex-col items-center px-5 py-24 md:px-12">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5">
            <Lock className="h-5 w-5 text-[#D4AF37]" />
          </div>
          <h1 className="mt-6 text-2xl font-semibold tracking-tight">Admin Access</h1>
          <p className="mt-2 text-sm text-zinc-400">Enter your dashboard password</p>

          <form
            onSubmit={login}
            data-testid="admin-login-form"
            className="mt-8 w-full rounded-2xl border border-white/10 bg-[#0F0F0F] p-6"
          >
            <Label htmlFor="pwd" className="mb-2 block text-sm text-zinc-300">
              Password
            </Label>
            <Input
              id="pwd"
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              data-testid="admin-password-input"
              placeholder="••••••••"
              className="h-12 rounded-lg border-white/10 bg-black/50 text-white focus-visible:border-[#D4AF37] focus-visible:ring-1 focus-visible:ring-[#D4AF37]"
            />
            <Button
              type="submit"
              data-testid="admin-login-button"
              className="mt-4 h-12 w-full rounded-lg bg-[#D4AF37] font-bold text-black hover:bg-[#F0C94F] transition-all"
            >
              Sign In
            </Button>
          </form>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <div className="mx-auto max-w-7xl px-5 py-12 md:px-12">
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#D4AF37]">
              Dashboard
            </p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">
              Leads <span className="text-zinc-500">({leads.length})</span>
            </h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={loadLeads}
              data-testid="refresh-leads-button"
              variant="outline"
              className="rounded-lg border-white/15 bg-transparent text-white hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/5"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              onClick={downloadCsv}
              data-testid="export-csv-button"
              className="rounded-lg bg-[#D4AF37] font-semibold text-black hover:bg-[#F0C94F]"
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
            <Button
              onClick={logout}
              data-testid="admin-logout-button"
              variant="ghost"
              className="text-zinc-400 hover:bg-white/5 hover:text-white"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>

        <div className="mt-8 overflow-hidden rounded-2xl border border-white/10 bg-[#0F0F0F]">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead className="border-b border-white/10 bg-black/40 text-left text-xs uppercase tracking-wider text-zinc-400">
                <tr>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Phone</Th>
                  <Th>Business</Th>
                  <Th>Revenue</Th>
                  <Th>Time in Business</Th>
                  <Th>Date</Th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-5 py-12 text-center text-zinc-500">
                      {loading ? "Loading…" : "No leads yet."}
                    </td>
                  </tr>
                )}
                {leads.map((l) => (
                  <tr
                    key={l.id}
                    className="border-b border-white/5 transition-colors hover:bg-white/[0.02]"
                  >
                    <Td className="font-medium text-white">{l.full_name}</Td>
                    <Td className="text-zinc-300">{l.email}</Td>
                    <Td className="text-zinc-300">{l.phone}</Td>
                    <Td className="text-zinc-300">{l.business_name}</Td>
                    <Td className="text-zinc-300">{l.monthly_revenue}</Td>
                    <Td className="text-zinc-300">{l.time_in_business}</Td>
                    <Td className="text-zinc-400">
                      {new Date(l.created_at).toLocaleString()}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const Th = ({ children }) => (
  <th className="px-5 py-3 font-semibold">{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-5 py-4 ${className}`}>{children}</td>
);
